import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { track } from '@quoter/web-core'
import { db } from '@/lib/db'
import type { Journey } from '@/lib/db-types'
import type { Phase } from '@/lib/plan'
import { useMe } from './useMe'

const key = (userId: string) => ['lowfodmaps', 'journey', userId] as const

export type JourneyView = {
  phase: Phase
  eliminationStartedOn: string | null
  /** When the person consented to LowFodmaps storing their health entries; null = not yet. */
  consentAt: string | null
}

const DEFAULT: JourneyView = { phase: 'elimination', eliminationStartedOn: null, consentAt: null }

function toView(row: Journey | null): JourneyView {
  return row
    ? { phase: row.phase, eliminationStartedOn: row.elimination_started_on, consentAt: row.health_consent_at }
    : DEFAULT
}

/** Where the person is in the diet. No row yet means "just starting". */
export function useJourney() {
  const me = useMe()
  const query = useQuery({
    queryKey: key(me?.userId ?? '__none'),
    enabled: !!me,
    queryFn: async (): Promise<JourneyView> => {
      const { data, error } = await db
        .from('journeys')
        .select('*')
        .eq('organization_id', me!.orgId)
        .eq('user_id', me!.userId)
        .maybeSingle()
      if (error) throw error
      return toView(data)
    },
  })
  return { journey: query.data ?? DEFAULT, loading: query.isLoading }
}

export function useSaveJourney() {
  const me = useMe()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (patch: Partial<JourneyView>) => {
      if (!me) throw new Error('not_signed_in')
      const current = queryClient.getQueryData<JourneyView>(key(me.userId)) ?? DEFAULT
      const next = { ...current, ...patch }
      const { error } = await db.from('journeys').upsert(
        {
          organization_id: me.orgId,
          user_id: me.userId,
          phase: next.phase,
          elimination_started_on: next.eliminationStartedOn,
          health_consent_at: next.consentAt,
        },
        { onConflict: 'organization_id,user_id' },
      )
      if (error) throw error
      return next
    },
    onSuccess: (next) => {
      if (me) queryClient.setQueryData(key(me.userId), next)
    },
  })
}

/**
 * Erase everything the person logged — diary, check-ins, challenges and the
 * journey row, which also withdraws their consent. Owner clauses on every
 * delete; RLS enforces the same pair. The account itself stays (billing lives
 * with it): closing that is a request to support.
 */
export function useDeleteMyData() {
  const me = useMe()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      if (!me) throw new Error('not_signed_in')
      for (const table of ['diary_entries', 'symptom_logs', 'challenges', 'journeys'] as const) {
        const { error } = await db.from(table).delete().eq('organization_id', me.orgId).eq('user_id', me.userId)
        if (error) throw error
      }
    },
    onSuccess: () => {
      track('data_deleted')
      return queryClient.invalidateQueries({ queryKey: ['lowfodmaps'] })
    },
  })
}
